import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
  forwardRef,
} from 'react';

import { Icon } from './icons.js';

export type SurfaceCardVariant = 'elevated' | 'filled' | 'outlined';

type SurfaceCardBaseProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly variant?: SurfaceCardVariant;
};

/** MD3 card (C1): elevated, filled, or outlined; one primary action when used as a link or button. */
export const Card = forwardRef<
  HTMLElement,
  SurfaceCardBaseProps &
    (
      | ({ readonly as?: 'article' } & React.HTMLAttributes<HTMLElement>)
      | ({ readonly as: 'button' } & ButtonHTMLAttributes<HTMLButtonElement>)
      | ({ readonly as: 'a' } & AnchorHTMLAttributes<HTMLAnchorElement>)
    )
>(function Card({ as = 'article', children, className, variant = 'filled', ...rest }, ref) {
  const classNames = ['saay-surface-card', 'saay-state', className].filter(Boolean).join(' ');
  const actionable = as === 'button' || as === 'a';
  if (as === 'button') {
    const props = rest as ButtonHTMLAttributes<HTMLButtonElement>;
    return (
      <button
        {...props}
        className={classNames}
        data-actionable="true"
        data-variant={variant}
        ref={ref as React.Ref<HTMLButtonElement>}
        type={props.type ?? 'button'}
      >
        {children}
      </button>
    );
  }
  if (as === 'a') {
    const props = rest as AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a
        {...props}
        className={classNames}
        data-actionable="true"
        data-variant={variant}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {children}
      </a>
    );
  }
  const props = rest as React.HTMLAttributes<HTMLElement>;
  return (
    <article
      {...props}
      className={classNames}
      data-variant={variant}
      ref={ref as React.Ref<HTMLElement>}
    >
      {children}
    </article>
  );
});

/** Vertical list container (C2). */
export function List({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label?: string;
}) {
  return (
    <ul {...(label === undefined ? {} : { 'aria-label': label })} className="saay-list">
      {children}
    </ul>
  );
}

type ListItemBase = {
  readonly leading?: ReactNode;
  readonly primary: ReactNode;
  readonly secondary?: ReactNode;
  readonly selected?: boolean;
  readonly tertiary?: ReactNode;
  readonly trailing?: ReactNode;
};

/** One list row (C2): up to three lines with optional leading and trailing slots. */
export function ListItem(
  props:
    | (ListItemBase & { readonly as?: 'div'; readonly onClick?: () => void })
    | (ListItemBase & { readonly as: 'button'; readonly onClick: () => void })
    | (ListItemBase & { readonly as: 'a'; readonly href: string }),
) {
  const {
    as = 'div',
    leading,
    primary,
    secondary,
    selected = false,
    tertiary,
    trailing,
    ...rest
  } = props;
  const href = 'href' in rest ? rest.href : undefined;
  const onClick = 'onClick' in rest ? rest.onClick : undefined;
  const interactive = as !== 'div' || onClick !== undefined;
  const className = 'saay-list-item saay-state';
  const body = (
    <>
      {leading === undefined ? null : <span className="saay-list-item__leading">{leading}</span>}
      <span className="saay-list-item__body">
        <span className="saay-list-item__primary">{primary}</span>
        {secondary === undefined ? null : (
          <span className="saay-list-item__secondary">{secondary}</span>
        )}
        {tertiary === undefined ? null : (
          <span className="saay-list-item__tertiary">{tertiary}</span>
        )}
      </span>
      {trailing === undefined ? null : <span className="saay-list-item__trailing">{trailing}</span>}
    </>
  );
  if (as === 'a' && href !== undefined) {
    return (
      <li>
        <a
          aria-current={selected ? 'true' : undefined}
          className={className}
          data-interactive="true"
          data-selected={selected || undefined}
          href={href}
        >
          {body}
        </a>
      </li>
    );
  }
  if (as === 'button' || onClick !== undefined) {
    return (
      <li>
        <button
          aria-current={selected ? 'true' : undefined}
          className={className}
          data-interactive="true"
          data-selected={selected || undefined}
          onClick={onClick}
          type="button"
        >
          {body}
        </button>
      </li>
    );
  }
  return (
    <li>
      <div className={className} data-selected={selected || undefined}>
        {body}
      </div>
    </li>
  );
}

export type AccordionItemDef = {
  readonly content: ReactNode;
  readonly key: string;
  readonly summary?: ReactNode;
  readonly title: ReactNode;
};

/** Expansion panels (C3) on Radix Accordion; collapsed header may show a one-line summary. */
export function AccordionPanel({
  defaultOpenKeys,
  items,
  headingLevel = 3,
  label,
  type = 'single',
}: {
  readonly defaultOpenKeys?: readonly string[];
  /** The header's heading level: 2 when the page has no heading between its title and the panel. */
  readonly headingLevel?: 2 | 3 | 4;
  readonly items: readonly AccordionItemDef[];
  readonly label: string;
  readonly type?: 'single' | 'multiple';
}) {
  const Heading = `h${String(headingLevel)}` as 'h2' | 'h3' | 'h4';
  if (type === 'multiple') {
    return (
      <AccordionPrimitive.Root
        aria-label={label}
        className="saay-accordion"
        defaultValue={[...(defaultOpenKeys ?? [])]}
        type="multiple"
      >
        {items.map((item) => (
          <AccordionPrimitive.Item className="saay-accordion__item" key={item.key} value={item.key}>
            <AccordionPrimitive.Header asChild>
              <Heading>
                <AccordionPrimitive.Trigger className="saay-accordion__trigger">
                  <span>
                    <strong>{item.title}</strong>
                    {item.summary === undefined ? null : (
                      <span className="saay-list-item__secondary"> — {item.summary}</span>
                    )}
                  </span>
                  <Icon className="saay-accordion__chevron" name="chevron-down" />
                </AccordionPrimitive.Trigger>
              </Heading>
            </AccordionPrimitive.Header>
            <AccordionPrimitive.Content className="saay-accordion__content">
              <div className="saay-accordion__panel">{item.content}</div>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        ))}
      </AccordionPrimitive.Root>
    );
  }
  const singleDefault = defaultOpenKeys?.[0];
  return (
    <AccordionPrimitive.Root
      aria-label={label}
      className="saay-accordion"
      collapsible
      {...(singleDefault === undefined ? {} : { defaultValue: singleDefault })}
      type="single"
    >
      {items.map((item) => (
        <AccordionPrimitive.Item className="saay-accordion__item" key={item.key} value={item.key}>
          <AccordionPrimitive.Header asChild>
            <Heading>
              <AccordionPrimitive.Trigger className="saay-accordion__trigger">
                <span>
                  <strong>{item.title}</strong>
                  {item.summary === undefined ? null : (
                    <span className="saay-list-item__secondary"> — {item.summary}</span>
                  )}
                </span>
                <Icon className="saay-accordion__chevron" name="chevron-down" />
              </AccordionPrimitive.Trigger>
            </Heading>
          </AccordionPrimitive.Header>
          <AccordionPrimitive.Content className="saay-accordion__content">
            <div className="saay-accordion__panel">{item.content}</div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      ))}
    </AccordionPrimitive.Root>
  );
}
